// ETF look-through EXPOSURE panel — expands held ETFs into constituents to show true
// exposure vs a benchmark (SPY). Point-in-time (current weights); a weekly snapshot
// accumulates history forward. Sector tilt + security active weights, side by side.
// See Plans/performance_and_risk_tabs.md §2.3b.
import React, { useEffect, useMemo, useState } from 'react';
import { tradingService } from '../../../services/tradingService';

const pct = (v, dp = 1) => (v == null || isNaN(v) ? '—' : `${(v * 100).toFixed(dp)}%`);
const signed = (v, dp = 1) => (v == null || isNaN(v) ? '—' : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(dp)}%`);
const clr = (v) => (v > 0 ? 'text-emerald-500' : v < 0 ? 'text-red-500' : 'text-gray-400');

// Diverging active-weight bar centered at 0 (over = green right, under = red left).
const TiltBar = ({ active, maxAbs }) => {
  const w = Math.min(50, (Math.abs(active) / (maxAbs || 1)) * 50);
  return (
    <div className="relative h-3.5 flex-1 min-w-[80px]">
      <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-300 dark:bg-gray-600" />
      {active >= 0
        ? <div className="absolute top-0.5 h-2.5 rounded-r bg-emerald-500/70" style={{ left: '50%', width: `${w}%` }} />
        : <div className="absolute top-0.5 h-2.5 rounded-l bg-red-500/70" style={{ right: '50%', width: `${w}%` }} />}
    </div>
  );
};

const TOP = 12;

const LookthroughPanel = ({ scope }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [benchmark] = useState('SPY'); // benchmark selectable later
  const [asOf, setAsOf] = useState(''); // '' = latest
  const [expandSec, setExpandSec] = useState(false);
  const [expandSym, setExpandSym] = useState(false);
  // Security-table sort: 'active' (magnitude) default, plus symbol / weight / bench.
  const [sortKey, setSortKey] = useState('active');
  const [sortDir, setSortDir] = useState('desc');
  const toggleSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir(k === 'symbol' ? 'asc' : 'desc'); }
  };
  const arrow = (k) => (sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  const key = JSON.stringify(scope || {});
  useEffect(() => {
    setLoading(true); setError(null);
    tradingService.getPortfolioLookthrough({ ...(scope || {}), benchmark, date: asOf || undefined })
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.error || 'Failed to load exposure'))
      .finally(() => setLoading(false));
  }, [key, benchmark, asOf]);   // eslint-disable-line react-hooks/exhaustive-deps

  const sectors = data?.by_sector || [];
  const secs = expandSec ? sectors : sectors.slice(0, TOP);
  const maxSecAbs = useMemo(() => Math.max(0.005, ...sectors.map((s) => Math.abs(s.active))), [sectors]);
  const syms = useMemo(() => {
    const arr = [...(data?.by_security || [])];
    arr.sort((a, b) => {
      if (sortKey === 'symbol') {
        const av = a.symbol || '', bv = b.symbol || '';
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      // 'active' sorts by MAGNITUDE (biggest tilt either way); weight/bench by value.
      const f = sortKey === 'active' ? Math.abs : (x) => x;
      const av = f(a[sortKey] ?? 0), bv = f(b[sortKey] ?? 0);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return arr;
  }, [data, sortKey, sortDir]);
  const shownSyms = expandSym ? syms : syms.slice(0, TOP);
  const notLooked = data?.not_looked_through || [];

  return (
    <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="text-[10px] uppercase tracking-wider text-gray-400">Exposure — look-through vs {data?.benchmark || benchmark}</div>
        <div className="flex items-center gap-2">
          {data?.date_min && data?.date_max && (
            <div className="flex items-center gap-1.5 text-[11px]">
              <span className="text-gray-400">As of</span>
              <input type="date" value={asOf} min={data.date_min} max={data.date_max}
                onChange={(e) => setAsOf(e.target.value)}
                className="bg-white dark:bg-[#0d1117] border border-gray-300 dark:border-gray-700 rounded px-1.5 py-0.5 text-[11px]" />
              {asOf && <button onClick={() => setAsOf('')} className="text-brand-gold hover:underline">Latest</button>}
            </div>
          )}
          <div className="text-[10px] text-gray-400">
            {data && data.total_usd > 0 && (
              <>look-through {pct(data.coverage_pct, 0)}{data.benchmark_as_of ? ` · ${benchmark} @ ${data.benchmark_as_of}` : ''}</>
            )}
          </div>
        </div>
      </div>

      {error && <div className="text-sm text-red-500">{error}</div>}
      {loading && !data && <div className="h-24 skeleton rounded-lg" />}

      {data && data.total_usd <= 0 && !loading && (
        <p className="text-sm text-gray-400 italic py-4">No live holdings in this scope to expand.</p>
      )}
      {data && !data.benchmark_as_of && data.total_usd > 0 && (
        <div className="mb-3 text-xs text-amber-500 bg-amber-500/8 border border-amber-500/30 rounded px-2 py-1.5">
          No {benchmark} holdings snapshot yet — the weekly ETF snapshot runs Saturday. Active-vs-benchmark fills in once it lands; effective exposure below is already correct.
        </div>
      )}

      {data && data.total_usd > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Sector tilt */}
          <div>
            <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-2">Sector tilt vs {benchmark}</div>
            <div className="grid grid-cols-[92px_1fr_54px] items-center gap-2 text-[10px] uppercase tracking-wider text-gray-400 mb-1">
              <div>Sector</div><div className="text-center">Under · Over</div><div className="text-right">Active</div>
            </div>
            <div className="space-y-1">
              {secs.map((s) => (
                <div key={s.sector} className="grid grid-cols-[92px_1fr_54px] items-center gap-2">
                  <div className="text-[11px] text-gray-600 dark:text-gray-300 truncate" title={s.sector}>{s.sector}</div>
                  <TiltBar active={s.active} maxAbs={maxSecAbs} />
                  <div className={`text-right text-[11px] tabular-nums ${clr(s.active)}`}>{signed(s.active)}</div>
                </div>
              ))}
            </div>
            {sectors.length > TOP && (
              <button onClick={() => setExpandSec((x) => !x)} className="mt-2 text-[11px] text-brand-gold hover:underline">
                {expandSec ? 'Show fewer' : `Show all ${sectors.length}`}
              </button>
            )}
          </div>

          {/* Security active weights */}
          <div>
            <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-2">Top active weights vs {benchmark}</div>
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-[12px] min-w-[320px]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-100 dark:border-gray-800 select-none">
                    <th className="text-left py-1.5 pr-2 cursor-pointer hover:text-gray-600 dark:hover:text-gray-300" onClick={() => toggleSort('symbol')}>Symbol{arrow('symbol')}</th>
                    <th className="text-right py-1.5 px-2 cursor-pointer hover:text-gray-600 dark:hover:text-gray-300" onClick={() => toggleSort('weight')}>Port{arrow('weight')}</th>
                    <th className="text-right py-1.5 px-2 cursor-pointer hover:text-gray-600 dark:hover:text-gray-300" onClick={() => toggleSort('bench')}>{benchmark}{arrow('bench')}</th>
                    <th className="text-right py-1.5 pl-2 cursor-pointer hover:text-gray-600 dark:hover:text-gray-300" onClick={() => toggleSort('active')}>Active{arrow('active')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800/60">
                  {shownSyms.map((r) => (
                    <tr key={r.symbol} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                      <td className="py-1.5 pr-2 text-gray-700 dark:text-gray-300">{r.symbol}
                        {r.sector && r.sector !== 'Unknown' && <span className="ml-1.5 text-[9px] text-gray-400">{r.sector}</span>}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums text-gray-600 dark:text-gray-400">{pct(r.weight)}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums text-gray-500">{pct(r.bench)}</td>
                      <td className={`py-1.5 pl-2 text-right tabular-nums font-medium ${clr(r.active)}`}>{signed(r.active)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {syms.length > TOP && (
              <button onClick={() => setExpandSym((x) => !x)} className="mt-2 text-[11px] text-brand-gold hover:underline">
                {expandSym ? 'Show fewer' : `Show all ${syms.length}`}
              </button>
            )}
          </div>
        </div>
      )}

      {notLooked.length > 0 && (
        <div className="mt-3 text-[11px] text-gray-400">
          Not looked through (no holdings snapshot):{' '}
          {notLooked.map((n) => <span key={n.symbol} className="inline-block mr-2">{n.symbol} <span className="text-gray-500">{pct(n.weight)}</span></span>)}
        </div>
      )}
    </div>
  );
};

export default LookthroughPanel;
